/**
 * FilterBar — the one filter toolbar (`KLQ-20260910-001` §A, §B).
 *
 * **The problem it exists for.** The tasks screen's filter panel was ~240px of controls drawn
 * *inside the scrolling region*, so on first paint a person saw the project name, a rail of
 * floors and eleven dropdowns, and not one row of the list they came for — and the moment they
 * scrolled far enough to read the list, the controls that would narrow it were gone. Two other
 * screens had each written their own bar beside it, differently.
 *
 * ```
 * ┌──────────────────────────────────────────────────────────┐
 * │ [Moje] [Po termíne] [Tento týždeň]            ⚙ Filtre(3)│
 * │ ⨯ Otvorené   ⨯ Vysoká   ⨯ Ana              Zrušiť všetko │
 * └──────────────────────────────────────────────────────────┘
 * ```
 *
 * FOUR DECISIONS
 *
 * 1. **It knows nothing about what a filter is.** `ChipSelect`'s rule, for the same reason:
 *    every word arrives as a prop and every narrowing arrives as a `FilterChip` the surface
 *    built from its own filter object. This component adds no message keys, holds no filter
 *    state, and cannot be the place where two screens disagree about what a filter *means* —
 *    it only decides what a filter bar *looks like*.
 * 2. **The second row exists only while something is applied.** An unfiltered screen is one
 *    line of toolbar and the list starts under it, which is the whole point.
 * 3. **The count counts fields, not values.** *Otvorené a Prebieha* is one narrowing with two
 *    answers — that is what `KLQ-20260825-003` bought — so it contributes 1, and it draws two
 *    chips. A number that grew when somebody added a second status would be counting the wrong
 *    noun, and it would make the *widest* question look like the most restricted one.
 * 4. **The saved chips stay outside the panel.** They are the questions people actually ask,
 *    they are one press, and they carry the loaded count of the list on screen. Burying them
 *    behind a trigger to save 32px would be saving the wrong 32px.
 *
 * **Where it goes: `Screen`'s `toolbar` slot**, whose entire contract is *never scrolls*
 * (`KLQ-20260826-001`). That slot is the answer to "make the filter sticky" — not
 * `position: sticky`, which is what a screen reaches for when it has not noticed the frame
 * already has a place for this.
 */

import { useRef, useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";

import type { ReactNode } from "react";

import { Button } from "../Button";
import { Drawer } from "../Drawer";
import { Popover } from "../Popover";
import { useMediaQuery, STACKED } from "@/lib/media";
import styles from "./FilterBar.module.css";

/**
 * One narrowing currently applied, as the person reads it.
 *
 * `field` is what the count counts and is not shown: two chips carrying the same `field` are
 * two answers to one question. `id` is only React's key and the remove button's identity.
 */
export interface FilterChip {
  readonly id: string;
  readonly field: string;
  readonly label: string;
  readonly onRemove: () => void;
}

/** How many *questions* are narrowed. Exported because it is what the trigger's number means
    and what a surface's own test asserts against. */
export function narrowedFields(summary: readonly FilterChip[]): number {
  return new Set(summary.map((chip) => chip.field)).size;
}

export interface FilterBarProps {
  /** The accessible name of the whole bar. */
  readonly legend: string;
  /** The saved-filter chip group, drawn by the surface. Optional — not every list has one. */
  readonly saved?: ReactNode;
  /**
   * What acts on the *result* rather than on the question: an export, a writer's button.
   * Drawn beside the trigger, because it belongs to the same row and must not scroll away
   * either — that is the whole reason the bar is in `Screen`'s `toolbar` slot.
   */
  readonly actions?: ReactNode;
  /** The trigger's word, without the number. */
  readonly triggerLabel: string;
  /** The panel's accessible name, and the drawer's heading under 900px. */
  readonly panelLabel: string;
  /** The drawer's ✕, under 900px. */
  readonly closeLabel: string;
  /** What is applied right now, one chip per value. */
  readonly summary: readonly FilterChip[];
  readonly clearLabel: string;
  readonly onClear: () => void;
  /** The accessible name of one chip's cross, given that chip's label. */
  readonly removeLabel: (label: string) => string;
  /** The fields. Rendered into the panel, never into the collapsed row. */
  readonly children: ReactNode;
}

export function FilterBar({
  legend,
  saved,
  actions,
  triggerLabel,
  panelLabel,
  closeLabel,
  summary,
  clearLabel,
  onClear,
  removeLabel,
  children,
}: FilterBarProps) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const stacked = useMediaQuery(STACKED);
  const count = narrowedFields(summary);

  /**
   * Closing, and where focus goes.
   *
   * `dismissed` is Escape — the person is stepping back, and the trigger is where they were
   * standing. `moved-on` is a press somewhere else: focus is already going where they chose
   * and pulling it back would be the panel arguing with them. That distinction is
   * `MenuPopup`'s and it is the same distinction here.
   */
  const close = (reason: "dismissed" | "moved-on"): void => {
    setOpen(false);
    if (reason === "dismissed") trigger.current?.focus();
  };

  const fields = <div className={styles.fields}>{children}</div>;

  return (
    <section className={styles.bar} aria-label={legend}>
      <div className={styles.row}>
        {saved === undefined ? <span /> : saved}

        <div className={styles.rowEnd}>
          {actions}
          <button
            ref={trigger}
            type="button"
            className={[styles.trigger, count > 0 ? styles.triggerOn : ""]
              .filter(Boolean)
              .join(" ")}
            aria-expanded={open}
            onClick={() => {
              setOpen((was) => !was);
            }}
          >
            <SlidersHorizontal size={14} strokeWidth={1.5} aria-hidden />
            {triggerLabel}
            {/* Inside the button, so it is part of the accessible name: a control read out as
                "Filters" while three are applied tells a screen-reader user the opposite of
                what the eye is told. */}
            {count > 0 ? <span className={styles.count}>{count}</span> : null}
          </button>
        </div>
      </div>

      {summary.length > 0 ? (
        <div className={styles.row}>
          <div className={styles.chips}>
            {summary.map((chip) => (
              <span key={chip.id} className={styles.chip}>
                {chip.label}
                <button
                  type="button"
                  className={styles.chipRemove}
                  aria-label={removeLabel(chip.label)}
                  onClick={chip.onRemove}
                >
                  <X size={12} strokeWidth={1.5} aria-hidden />
                </button>
              </span>
            ))}
          </div>
          <Button size="sm" variant="tertiary" onClick={onClear}>
            {clearLabel}
          </Button>
        </div>
      ) : null}

      {/* Stacked, a popover anchored to a control in a one-column layout is a drawer — which is
          what every other panel in this product already is at this width. Above 900px the
          fields float beside the trigger and the list stays readable behind them. */}
      {!open ? null : stacked ? (
        <Drawer
          title={panelLabel}
          closeLabel={closeLabel}
          onClose={() => {
            close("dismissed");
          }}
        >
          {fields}
        </Drawer>
      ) : (
        <Popover
          anchor={trigger.current}
          align="end"
          label={panelLabel}
          onClose={close}
        >
          {fields}
        </Popover>
      )}
    </section>
  );
}
